import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Receipt, Plus, Trash2, Download, Users, FileText,
  Calendar, Hash, Search, Pencil, Repeat, CalendarClock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { SendReportButton } from '../components/common/SendReportButton';
import {
  API, authHeaders, DhSymbol, fmtAmount, fmtDateLong, brandMeta,
  ConfirmDialog, openBlob,
} from './invoices/utils';
import ClientsTable from './invoices/ClientsTable';
import CreateInvoiceDialog from './invoices/CreateInvoiceDialog';
import RecurringSettingsDialog from './invoices/RecurringSettingsDialog';

/* ────────────────────────────────────────────────────────────────
   Page
   ──────────────────────────────────────────────────────────────── */
const Invoices = () => {
  const [tab, setTab] = useState('history');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [repeatingInvoice, setRepeatingInvoice] = useState(null);
  const [showClient, setShowClient] = useState(false);
  const [search, setSearch] = useState('');
  const [recurringSettings, setRecurringSettings] = useState({ enabled: false, day_of_month: 1, recipients: [] });
  const [showRecurring, setShowRecurring] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [invR, cliR, brR, recR] = await Promise.all([
        axios.get(`${API}/api/invoices`, { headers: authHeaders() }),
        axios.get(`${API}/api/invoices/clients`, { headers: authHeaders() }),
        axios.get(`${API}/api/invoices/brands`, { headers: authHeaders() }),
        axios.get(`${API}/api/invoices/recurring/settings`, { headers: authHeaders() }),
      ]);
      setInvoices(invR.data.invoices || []);
      setClients(cliR.data.clients || []);
      setBrands(brR.data.brands || []);
      setRecurringSettings(recR.data || { enabled: false, day_of_month: 1, recipients: [] });
    } catch (e) {
      toast({
        title: 'Failed to load',
        description: e?.response?.data?.detail || 'Check permissions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(i => {
      if (brandFilter !== 'all' && (i.brand || 'vynce') !== brandFilter) return false;
      if (!q) return true;
      return i.invoice_number?.toLowerCase().includes(q)
        || i.client?.name?.toLowerCase().includes(q)
        || i.period_label?.toLowerCase().includes(q);
    });
  }, [invoices, search, brandFilter]);

  const [pendingDelete, setPendingDelete] = useState(null);
  const deleteInvoice = (inv) => setPendingDelete(inv);
  const confirmDeleteInvoice = async () => {
    const inv = pendingDelete;
    setPendingDelete(null);
    if (!inv) return;
    try {
      await axios.delete(`${API}/api/invoices/${inv.id}`, { headers: authHeaders() });
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
      toast({ title: 'Invoice deleted', description: inv.invoice_number });
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: e?.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const download = async (inv) => {
    try {
      const r = await axios.get(`${API}/api/invoices/${inv.id}/download`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      openBlob(blob, `${inv.invoice_number.replace(/\//g, '_')}.pdf`);
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const toggleRecurring = async (inv, value) => {
    setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, recurring: value } : i));
    try {
      await axios.patch(`${API}/api/invoices/${inv.id}/recurring`, { recurring: value }, { headers: authHeaders() });
      toast({ title: value ? 'Repeat enabled' : 'Repeat disabled', description: `${inv.invoice_number} — ${value ? 'will auto-repeat monthly' : 'no longer repeats'}` });
    } catch (e) {
      setInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, recurring: !value } : i));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const recurringCount = useMemo(() => invoices.filter(i => i.recurring).length, [invoices]);

  const toggleMaster = async (value) => {
    setRecurringSettings(prev => ({ ...prev, enabled: value }));
    try {
      await axios.put(`${API}/api/invoices/recurring/settings`,
        { enabled: value, day_of_month: recurringSettings.day_of_month || 1, recipients: recurringSettings.recipients || [] },
        { headers: authHeaders() });
      toast({ title: value ? 'Auto-invoicing ON' : 'Auto-invoicing paused', description: value ? `Recurring invoices generate on the ${recurringSettings.day_of_month || 1} of each month.` : 'No invoices will auto-generate until re-enabled.' });
    } catch (e) {
      setRecurringSettings(prev => ({ ...prev, enabled: !value }));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-rose-100 p-2.5 rounded-xl">
            <Receipt className="h-6 w-6 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <p className="text-sm text-gray-500">Generate, download &amp; track client invoices</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-1.5" data-testid="auto-invoicing-pill">
            <CalendarClock className="h-4 w-4 text-rose-600" />
            <div className="leading-tight">
              <p className="text-xs font-semibold text-gray-900">Auto-invoicing</p>
              <p className="text-[10px] text-gray-500">{recurringCount} set to repeat</p>
            </div>
            <Switch
              checked={!!recurringSettings.enabled}
              onCheckedChange={toggleMaster}
              data-testid="auto-invoicing-master-switch"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRecurring(true)}
              className="h-7 px-2 text-rose-700 hover:bg-rose-100"
              data-testid="auto-invoicing-configure-btn"
            >
              Configure
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowClient(true)}
            data-testid="manage-clients-btn"
          >
            <Users className="h-4 w-4 mr-1" /> Clients
          </Button>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="create-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New Invoice
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="h-4 w-4 mr-1" /> Invoice History
            <Badge variant="secondary" className="ml-2">{invoices.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">
            <Users className="h-4 w-4 mr-1" /> Clients
            <Badge variant="secondary" className="ml-2">{clients.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by number, client, period…"
                className="pl-9"
                data-testid="invoice-search"
              />
            </div>
            <div className="inline-flex bg-gray-100 rounded-lg p-0.5 text-xs" data-testid="brand-filter">
              {[{ key: 'all', label: 'All brands' }, ...brands.map(b => ({ key: b.key, label: b.label }))].map(b => (
                <button
                  key={b.key}
                  onClick={() => setBrandFilter(b.key)}
                  className={`px-3 py-1.5 rounded-md font-medium transition ${brandFilter === b.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid={`brand-filter-${b.key}`}
                >{b.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <Receipt className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 mb-3">
                  {invoices.length === 0 ? 'No invoices yet.' : 'No matches for your search.'}
                </p>
                {invoices.length === 0 && (
                  <Button onClick={() => setShowCreate(true)} className="bg-rose-600 hover:bg-rose-700">
                    <Plus className="h-4 w-4 mr-1" /> Create your first invoice
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                    <tr>
                      <th className="text-left px-4 py-3"><Hash className="h-3 w-3 inline mr-1" />Number</th>
                      <th className="text-left px-4 py-3">Brand</th>
                      <th className="text-left px-4 py-3">Client</th>
                      <th className="text-left px-4 py-3"><Calendar className="h-3 w-3 inline mr-1" />Date</th>
                      <th className="text-left px-4 py-3">Period</th>
                      <th className="text-right px-4 py-3">Subtotal</th>
                      <th className="text-right px-4 py-3">VAT</th>
                      <th className="text-right px-4 py-3">Grand Total</th>
                      <th className="text-center px-4 py-3">Repeat</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(inv => (
                      <tr
                        key={inv.id}
                        className="border-t hover:bg-rose-50/30 transition-colors"
                        data-testid={`invoice-row-${inv.invoice_number.replace(/\//g, '-')}`}
                      >
                        <td className="px-4 py-3 font-mono font-semibold text-rose-700 whitespace-nowrap">
                          {inv.invoice_number}
                          {inv.status === 'draft' && (
                            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-300 text-[9px] align-middle" data-testid={`draft-badge-${inv.invoice_number.replace(/\//g, '-')}`}>
                              DRAFT
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={brandMeta(inv.brand).cls}>
                            {brandMeta(inv.brand).label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{inv.client?.name || '—'}</p>
                          {inv.client?.email && (
                            <p className="text-[10px] text-gray-400">{inv.client.email}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateLong(inv.invoice_date)}</td>
                        <td className="px-4 py-3">
                          {inv.period_type && inv.period_type !== 'none' ? (
                            <Badge variant="outline" className="capitalize">
                              {inv.period_type}
                            </Badge>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                          {inv.period_label && (
                            <span className="text-xs text-gray-500 ml-2">{inv.period_label}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                          <DhSymbol className="text-[10px] mr-0.5 text-gray-400" />{fmtAmount(inv.totals?.subtotal)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                          {inv.tax_percent ? (
                            <>
                              <DhSymbol className="text-[10px] mr-0.5 text-gray-400" />{fmtAmount(inv.totals?.tax_amount)}
                              <span className="text-[10px] text-gray-400 ml-1">({inv.tax_percent}%)</span>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-rose-700 whitespace-nowrap">
                          <DhSymbol className="text-xs mr-0.5" />{fmtAmount(inv.totals?.grand_total)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <Switch
                              checked={!!inv.recurring}
                              onCheckedChange={(v) => toggleRecurring(inv, v)}
                              data-testid={`recurring-toggle-${inv.invoice_number.replace(/\//g, '-')}`}
                            />
                            {inv.recurring && (
                              <span className="text-[9px] text-emerald-600 font-medium">Monthly</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => download(inv)}
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 mr-1"
                            data-testid={`download-invoice-${inv.invoice_number.replace(/\//g, '-')}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" /> PDF
                          </Button>
                          <SendReportButton
                            reportUrl={`/api/invoices/${inv.id}/download`}
                            filename={`${inv.invoice_number.replace(/\//g, '_')}.pdf`}
                            defaultSubject={`Invoice ${inv.invoice_number}${inv.client?.name ? ' — ' + inv.client.name : ''}`}
                            label=""
                            iconOnly
                            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 mr-1"
                            testid={`email-invoice-${inv.invoice_number.replace(/\//g, '-')}`}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRepeatingInvoice(inv)}
                            className="text-emerald-600 hover:bg-emerald-50 mr-1"
                            data-testid={`repeat-invoice-${inv.invoice_number.replace(/\//g, '-')}`}
                            title="Repeat for next month"
                          >
                            <Repeat className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingInvoice(inv)}
                            className="text-gray-600 hover:bg-gray-100 mr-1"
                            data-testid={`edit-invoice-${inv.invoice_number.replace(/\//g, '-')}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteInvoice(inv)}
                            className="text-rose-600 hover:bg-rose-50"
                            data-testid={`delete-invoice-${inv.invoice_number.replace(/\//g, '-')}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="clients" className="mt-4 space-y-3">
          <ClientsTable
            clients={clients}
            reload={loadAll}
          />
        </TabsContent>
      </Tabs>

      <CreateInvoiceDialog
        open={showCreate}
        onOpenChange={(v) => { setShowCreate(v); if (!v) loadAll(); }}
        clients={clients}
        brands={brands}
        onClientCreated={loadAll}
      />
      <CreateInvoiceDialog
        open={!!editingInvoice}
        onOpenChange={(v) => { if (!v) { setEditingInvoice(null); loadAll(); } }}
        clients={clients}
        brands={brands}
        onClientCreated={loadAll}
        editing={editingInvoice}
      />
      <CreateInvoiceDialog
        open={!!repeatingInvoice}
        onOpenChange={(v) => { if (!v) { setRepeatingInvoice(null); loadAll(); } }}
        clients={clients}
        brands={brands}
        onClientCreated={loadAll}
        repeating={repeatingInvoice}
      />
      <RecurringSettingsDialog
        open={showRecurring}
        onOpenChange={setShowRecurring}
        settings={recurringSettings}
        reload={loadAll}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
        title={`Delete invoice ${pendingDelete?.invoice_number || ''}?`}
        description="This cannot be undone."
        onConfirm={confirmDeleteInvoice}
        testid="delete-invoice-dialog"
      />
      <Dialog open={showClient} onOpenChange={setShowClient}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Clients</DialogTitle>
          </DialogHeader>
          <ClientsTable clients={clients} reload={loadAll} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
